/**
 * SchoolHousing — per-tenant Hostel / Accommodation management.
 * Tabs: Overview · Blocks · Rooms · Allocations
 */
import React, { useState } from 'react';
import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Building2, BedDouble, Users, Plus, LogOut, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  try {
    const s = localStorage.getItem('eventflow-auth');
    if (s) {
      const p = JSON.parse(s);
      if (p?.state?.token) return { Authorization: `Bearer ${p.state.token}` };
    }
  } catch { /* noop */ }
  const legacy = getToken();
  return legacy ? { Authorization: `Bearer ${legacy}` } : {};
};
const api = {
  overview: (tid) => axios.get(`${API_URL}/api/school/tenants/${tid}/housing/overview`, { headers: authHeaders() }).then(r => r.data),
  listBlocks: (tid) => axios.get(`${API_URL}/api/school/tenants/${tid}/housing/blocks`, { headers: authHeaders() }).then(r => r.data),
  createBlock: (tid, data) => axios.post(`${API_URL}/api/school/tenants/${tid}/housing/blocks`, data, { headers: authHeaders() }).then(r => r.data),
  deleteBlock: (tid, id) => axios.delete(`${API_URL}/api/school/tenants/${tid}/housing/blocks/${id}`, { headers: authHeaders() }).then(r => r.data),
  listRooms: (tid, blockId) => axios.get(`${API_URL}/api/school/tenants/${tid}/housing/rooms${blockId ? `?block_id=${blockId}` : ''}`, { headers: authHeaders() }).then(r => r.data),
  createRoom: (tid, data) => axios.post(`${API_URL}/api/school/tenants/${tid}/housing/rooms`, data, { headers: authHeaders() }).then(r => r.data),
  deleteRoom: (tid, id) => axios.delete(`${API_URL}/api/school/tenants/${tid}/housing/rooms/${id}`, { headers: authHeaders() }).then(r => r.data),
  listAllocations: (tid) => axios.get(`${API_URL}/api/school/tenants/${tid}/housing/allocations`, { headers: authHeaders() }).then(r => r.data),
  createAllocation: (tid, data) => axios.post(`${API_URL}/api/school/tenants/${tid}/housing/allocations`, data, { headers: authHeaders() }).then(r => r.data),
  checkout: (tid, id) => axios.post(`${API_URL}/api/school/tenants/${tid}/housing/allocations/${id}/checkout`, {}, { headers: authHeaders() }).then(r => r.data),
  listStudents: (tid) => axios.get(`${API_URL}/api/school/tenants/${tid}/students`, { headers: authHeaders() }).then(r => r.data),
};

export const SchoolHousing = ({ tenantId }) => {
  const [tab, setTab] = useState('overview');
  const qc = useQueryClient();
  const { data: overview } = useQuery({ queryKey: ['housing-overview', tenantId], queryFn: () => api.overview(tenantId) });
  const { data: blocks = [] } = useQuery({ queryKey: ['housing-blocks', tenantId], queryFn: () => api.listBlocks(tenantId) });
  const { data: rooms = [] } = useQuery({ queryKey: ['housing-rooms', tenantId], queryFn: () => api.listRooms(tenantId) });
  const { data: allocations = [] } = useQuery({ queryKey: ['housing-allocations', tenantId], queryFn: () => api.listAllocations(tenantId) });
  const { data: students = [] } = useQuery({ queryKey: ['housing-students', tenantId], queryFn: () => api.listStudents(tenantId) });

  const refresh = () => {
    ['housing-overview', 'housing-blocks', 'housing-rooms', 'housing-allocations'].forEach(k =>
      qc.invalidateQueries({ queryKey: [k, tenantId] })
    );
  };

  return (
    <div className="space-y-4" data-testid="school-housing">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2"><Building2 className="h-5 w-5 text-indigo-500" /> Housing & Hostels</h2>
          <p className="text-sm text-slate-500">Blocks, rooms, and student bed allocations for this school.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="housing-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="blocks" data-testid="housing-tab-blocks">Blocks</TabsTrigger>
          <TabsTrigger value="rooms" data-testid="housing-tab-rooms">Rooms</TabsTrigger>
          <TabsTrigger value="allocations" data-testid="housing-tab-allocations">Allocations</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Kpi label="Blocks" value={overview?.total_blocks ?? 0} />
            <Kpi label="Rooms" value={overview?.total_rooms ?? 0} />
            <Kpi label="Capacity" value={overview?.total_capacity ?? 0} />
            <Kpi label="Occupied" value={overview?.occupied ?? 0} accent="indigo" />
            <Kpi label="Occupancy" value={`${overview?.occupancy_rate ?? 0}%`} accent="emerald" />
          </div>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Block Breakdown</CardTitle></CardHeader>
            <CardContent className="space-y-1">
              {(overview?.blocks || []).length === 0 && <p className="text-sm text-slate-500">No blocks yet — create one under the Blocks tab.</p>}
              {(overview?.blocks || []).map((b) => (
                <div key={b.block_id} className="flex items-center gap-3 py-1.5 border-b last:border-0">
                  <span className="text-sm font-medium flex-1 truncate">{b.name}</span>
                  <span className="text-xs text-slate-500">{b.occupied}/{b.capacity}</span>
                  <div className="w-32 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-indigo-500" style={{ width: `${b.capacity ? (b.occupied / b.capacity) * 100 : 0}%` }} />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="blocks" className="space-y-3">
          <CreateBlockCard tenantId={tenantId} onCreated={refresh} />
          <div className="grid md:grid-cols-2 gap-3">
            {blocks.map((b) => (
              <Card key={b.id} data-testid={`housing-block-${b.id}`}>
                <CardContent className="py-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center"><Building2 className="h-5 w-5" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{b.name} <Badge variant="outline" className="ml-1 text-[10px]">{b.gender}</Badge></p>
                    <p className="text-[11px] text-slate-500">{b.room_count} rooms · {b.occupancy}/{b.capacity} beds</p>
                  </div>
                  <Button size="sm" variant="outline"
                          onClick={() => { if (window.confirm('Delete this block?')) api.deleteBlock(tenantId, b.id).then(refresh).catch(e => toast.error(e?.response?.data?.detail || 'Failed')); }}
                          data-testid={`delete-block-${b.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="rooms" className="space-y-3">
          <CreateRoomCard tenantId={tenantId} blocks={blocks} onCreated={refresh} />
          <Card>
            <CardContent className="py-3">
              {rooms.length === 0 && <p className="text-sm text-slate-500 py-4 text-center">No rooms yet.</p>}
              {rooms.map((r) => (
                <div key={r.id} className="flex items-center gap-3 py-2 border-b last:border-0" data-testid={`housing-room-${r.id}`}>
                  <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center"><BedDouble className="h-4 w-4" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{r.block_name} / Room {r.room_number}</p>
                    <p className="text-[11px] text-slate-500">{r.room_type} · capacity {r.capacity} · {r.occupants} occupant(s)</p>
                  </div>
                  <Button size="sm" variant="outline"
                          onClick={() => { if (window.confirm('Delete this room?')) api.deleteRoom(tenantId, r.id).then(refresh).catch(e => toast.error(e?.response?.data?.detail || 'Failed')); }}
                          data-testid={`delete-room-${r.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="allocations" className="space-y-3">
          <CreateAllocationCard tenantId={tenantId} rooms={rooms} students={students} onCreated={refresh} />
          <Card>
            <CardContent className="py-3">
              {allocations.length === 0 && <p className="text-sm text-slate-500 py-4 text-center">No allocations yet.</p>}
              {allocations.map((a) => (
                <div key={a.id} className="flex items-center gap-3 py-2 border-b last:border-0" data-testid={`housing-allocation-${a.id}`}>
                  <div className="w-9 h-9 rounded-lg bg-fuchsia-100 text-fuchsia-600 flex items-center justify-center"><Users className="h-4 w-4" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{a.student_name || a.student_id} <span className="text-[11px] text-slate-500">({a.admission_number || '—'})</span></p>
                    <p className="text-[11px] text-slate-500">{a.block_name || '—'} / Room {a.room_number || a.room_id} {a.bed_number ? `· Bed ${a.bed_number}` : ''}</p>
                  </div>
                  {a.status === 'active' && (
                    <Button size="sm" variant="outline"
                            onClick={() => api.checkout(tenantId, a.id).then(() => { toast.success('Checked out'); refresh(); }).catch(e => toast.error(e?.response?.data?.detail || 'Failed'))}
                            data-testid={`checkout-allocation-${a.id}`}>
                      <LogOut className="h-4 w-4 mr-1" /> Checkout
                    </Button>
                  )}
                  {a.status !== 'active' && <Badge variant="outline" className="text-[10px]">{a.status}</Badge>}
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const Kpi = ({ label, value, accent }) => (
  <Card data-testid={`housing-kpi-${label.toLowerCase()}`}>
    <CardContent className="py-4">
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${accent === 'indigo' ? 'text-indigo-600' : accent === 'emerald' ? 'text-emerald-600' : 'text-slate-900'}`}>{value}</p>
    </CardContent>
  </Card>
);

const CreateBlockCard = ({ tenantId, onCreated }) => {
  const [form, setForm] = useState({ name: '', gender: 'mixed', description: '' });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.name.trim()) return toast.error('Name required');
    setSaving(true);
    try { await api.createBlock(tenantId, form); toast.success('Block created'); setForm({ name: '', gender: 'mixed', description: '' }); onCreated(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Plus className="h-4 w-4" /> Add Block</CardTitle></CardHeader>
      <CardContent className="grid md:grid-cols-4 gap-2">
        <Input placeholder="Block name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="housing-block-name-input" />
        <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
          <SelectTrigger data-testid="housing-block-gender-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="male">Male</SelectItem>
            <SelectItem value="female">Female</SelectItem>
            <SelectItem value="mixed">Mixed</SelectItem>
          </SelectContent>
        </Select>
        <Input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <Button onClick={submit} disabled={saving} data-testid="housing-create-block-btn">Create</Button>
      </CardContent>
    </Card>
  );
};

const CreateRoomCard = ({ tenantId, blocks, onCreated }) => {
  const [form, setForm] = useState({ block_id: '', room_number: '', capacity: 2, room_type: 'standard' });
  const submit = async () => {
    if (!form.block_id || !form.room_number) return toast.error('Block and room number required');
    try { await api.createRoom(tenantId, form); toast.success('Room created'); setForm({ block_id: form.block_id, room_number: '', capacity: 2, room_type: 'standard' }); onCreated(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Plus className="h-4 w-4" /> Add Room</CardTitle></CardHeader>
      <CardContent className="grid md:grid-cols-5 gap-2">
        <Select value={form.block_id} onValueChange={(v) => setForm({ ...form, block_id: v })}>
          <SelectTrigger data-testid="housing-room-block-select"><SelectValue placeholder="Pick a block" /></SelectTrigger>
          <SelectContent>
            {blocks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input placeholder="Room #" value={form.room_number} onChange={(e) => setForm({ ...form, room_number: e.target.value })} data-testid="housing-room-number-input" />
        <Input type="number" min="1" placeholder="Capacity" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 1 })} />
        <Select value={form.room_type} onValueChange={(v) => setForm({ ...form, room_type: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="standard">Standard</SelectItem>
            <SelectItem value="ensuite">Ensuite</SelectItem>
            <SelectItem value="dormitory">Dormitory</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={submit} data-testid="housing-create-room-btn">Create</Button>
      </CardContent>
    </Card>
  );
};

const CreateAllocationCard = ({ tenantId, rooms, students, onCreated }) => {
  const [form, setForm] = useState({ student_id: '', room_id: '', bed_number: '' });
  const submit = async () => {
    if (!form.student_id || !form.room_id) return toast.error('Student and room required');
    try { await api.createAllocation(tenantId, form); toast.success('Allocated'); setForm({ student_id: '', room_id: '', bed_number: '' }); onCreated(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const studentList = Array.isArray(students) ? students : (students?.students || []);
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Plus className="h-4 w-4" /> Allocate Student</CardTitle></CardHeader>
      <CardContent className="grid md:grid-cols-4 gap-2">
        <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
          <SelectTrigger data-testid="housing-alloc-student-select"><SelectValue placeholder="Pick a student" /></SelectTrigger>
          <SelectContent className="max-h-64 overflow-auto">
            {studentList.slice(0, 200).map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.admission_number})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={form.room_id} onValueChange={(v) => setForm({ ...form, room_id: v })}>
          <SelectTrigger data-testid="housing-alloc-room-select"><SelectValue placeholder="Pick a room" /></SelectTrigger>
          <SelectContent className="max-h-64 overflow-auto">
            {rooms.map((r) => (
              <SelectItem key={r.id} value={r.id} disabled={r.occupants >= r.capacity}>
                {r.block_name} / {r.room_number} ({r.occupants}/{r.capacity})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input placeholder="Bed # (optional)" value={form.bed_number} onChange={(e) => setForm({ ...form, bed_number: e.target.value })} />
        <Button onClick={submit} data-testid="housing-allocate-btn">Allocate</Button>
      </CardContent>
    </Card>
  );
};

export default SchoolHousing;
